import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { Router } from '@angular/router';

/**
 * ErrorInterceptor
 *
 * Single point of error notification for ALL HTTP errors.
 * - Extracts the backend `message` from the response body and shows it.
 * - Handles auth errors (401/403) with redirect.
 * - Components should NOT show their own error toasts — the interceptor
 *   handles it. NotificationService has a cooldown to suppress duplicates.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private notification = inject(NotificationService);
  private router = inject(Router);

  /** URLs that handle their own errors (login, signup, etc.) */
  private silentUrls = [
    'signin',
    'signup',
    'verify-otp',
    'forgot-password',
    'reset-password'
  ];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, req))
    );
  }

  private handleError(error: HttpErrorResponse, req: HttpRequest<unknown>): Observable<never> {
    const isSilent = this.silentUrls.some(url => req.url.includes(url));

    if (!isSilent) {
      if (error.status === 401 || error.status === 403) {
        this.handleAuthError(error);
      } else {
        const message = this.extractMessage(error);
        this.notification.error(message);
      }
    }

    return throwError(() => error);
  }

  /**
   * Extract the user-facing message from the backend response.
   * Backend shape: { success, message, data, status, code }
   */
  private extractMessage(error: HttpErrorResponse): string {
    const body = error.error;

    if (body && typeof body === 'object') {
      // .NET validation errors — check FIRST so we get specific field errors
      // instead of the generic title like "One or more validation errors occurred."
      if (body.errors && typeof body.errors === 'object') {
        return this.extractValidationErrors(body.errors);
      }
      // Primary: backend `message` field
      if (body.message && typeof body.message === 'string') {
        return body.message;
      }
      // Fallback: .NET `title` field
      if (body.title && typeof body.title === 'string') {
        return body.title;
      }
    }

    // Plain string body
    if (typeof body === 'string' && body.length > 0 && body.length < 500) {
      return body;
    }

    // Fallback by status code
    if (error.status === 0) {
      return 'Unable to connect to server. Please check your connection.';
    }
    if (error.status === 404) {
      return 'The requested resource was not found.';
    }
    if (error.status >= 500) {
      return 'A server error occurred. Please try again later.';
    }

    return 'Something went wrong. Please try again.';
  }

  private extractValidationErrors(errors: unknown): string {
    if (Array.isArray(errors)) {
      return errors.join('. ');
    }
    if (typeof errors === 'object' && errors !== null) {
      const msgs: string[] = [];
      for (const val of Object.values(errors)) {
        if (Array.isArray(val)) msgs.push(...val);
        else if (typeof val === 'string') msgs.push(val);
      }
      if (msgs.length) return msgs.join('. ');
    }
    return 'Validation failed. Please check your input.';
  }

  private handleAuthError(error: HttpErrorResponse): void {
    localStorage.removeItem('token');
    const message = error.status === 401
      ? 'Your session has expired. Please log in again.'
      : 'You do not have permission to access this resource.';
    this.notification.warning(message, 'Authentication Required');
    this.router.navigate(['/authentication/login'], {
      queryParams: { returnUrl: this.router.url }
    });
  }
}
